import { useCallback, useMemo, useState } from "react";

import { cn } from "../lib/cn";

export interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  align?: "left" | "right" | "center";
  render?: (value: unknown, row: Row) => React.ReactNode;
}

export type Row = Record<string, unknown>;

export interface TableProps {
  columns: Column[];
  data: Row[];
  sortable?: boolean;
  pageSize?: number;
  emptyText?: string;
  className?: string;
}

export function Table({
  columns,
  data,
  sortable = false,
  pageSize,
  emptyText = "No data",
  className,
}: TableProps) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey],
  );

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal === bVal) return 0;
      const cmp =
        aVal !== null &&
        aVal !== undefined &&
        bVal !== null &&
        bVal !== undefined &&
        aVal < bVal
          ? -1
          : 1;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const pageData = pageSize
    ? sortedData.slice(page * pageSize, (page + 1) * pageSize)
    : sortedData;

  const totalPages = pageSize ? Math.ceil(data.length / pageSize) : 1;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  "text-gold px-3 py-2 font-mono text-[10px] font-normal uppercase tracking-[0.15em]",
                  "border-gold-dim border-b",
                  col.align === "right" && "text-right",
                  col.align === "center" && "text-center",
                  (sortable || col.sortable) &&
                    "hover:text-gold/80 cursor-pointer select-none",
                )}
                onClick={
                  sortable || col.sortable
                    ? () => {
                        handleSort(col.key);
                      }
                    : undefined
                }
              >
                {col.label}
                {sortKey === col.key && (
                  <span className="ml-1">
                    {sortDir === "asc" ? "\u2191" : "\u2193"}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="text-text-muted px-3 py-8 text-center font-mono text-sm"
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            pageData.map((row, i) => (
              <tr
                key={i}
                className="transition-colors duration-150 hover:bg-[rgba(201,168,76,0.025)]"
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      "text-text-dim px-3 py-2 font-sans text-[15px]",
                      "border-b border-[rgba(255,255,255,0.04)]",
                      col.align === "right" && "text-right",
                      col.align === "center" && "text-center",
                    )}
                  >
                    {col.render
                      ? col.render(row[col.key], row)
                      : /* eslint-disable-next-line @typescript-eslint/no-base-to-string */
                        String(row[col.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {pageSize && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={page === 0}
            onClick={() => {
              setPage((p) => p - 1);
            }}
            className="text-gold disabled:text-text-muted font-mono text-[10px] disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="text-text-dim font-mono text-[10px]">
            {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => {
              setPage((p) => p + 1);
            }}
            className="text-gold disabled:text-text-muted font-mono text-[10px] disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
